
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Plan } from "./types";
import { PlanCard } from "./PlanCard";
import { shouldDisablePlan, getDisabledReason } from "./subscriptionUtils";
import { getApplicableDiscount } from "./discountUtils";

interface PlansSectionProps {
  plans: Plan[];
  loading: boolean;
  isLoggedIn: boolean;
  subscribing: boolean;
  userSubscriptions: any[];
  productId: string | undefined;
  onSubscribe: (plan: Plan) => Promise<void>;
}

export function PlansSection({ 
  plans, 
  loading, 
  isLoggedIn, 
  subscribing, 
  userSubscriptions, 
  productId,
  onSubscribe 
}: PlansSectionProps) {
  // Sort the plans in the order: Monthly, Yearly, One-Time
  const sortedPlans = [...plans].sort((a, b) => {
    const orderMap: Record<string, number> = {
      'Monthly': 1,
      'Yearly': 2,
      'One-Time': 3
    };
    
    const orderA = orderMap[a.plan_name] || 99;
    const orderB = orderMap[b.plan_name] || 99;
    
    return orderA - orderB;
  });

  const [schoolId, setSchoolId] = useState<string | null>(null);
  const [planDiscounts, setPlanDiscounts] = useState<Record<string, any>>({});
  const [loadingDiscounts, setLoadingDiscounts] = useState(false);

  const [hasUsedTrial, setHasUsedTrial] = useState(false);

  // Fetch the user's school ID
  useEffect(() => {
    const fetchSchoolId = async () => {
      if (!isLoggedIn) {
        console.log('User not logged in, skipping school ID fetch');
        return;
      }
      
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user?.email) {
          console.log('No user email available');
          return;
        }

        const { data: schoolUser } = await supabase
          .from('school_users')
          .select('school_id')
          .eq('email', user.email)
          .maybeSingle();

        if (schoolUser?.school_id) {
          setSchoolId(schoolUser.school_id);
          console.log('School ID set:', schoolUser.school_id);
        } else {
          console.log('No school ID found for user');
        }
      } catch (error) {
        console.error('Error fetching school ID:', error);
      }
    };

    fetchSchoolId();
  }, [isLoggedIn]);

  // Check if school has already used their trial
  useEffect(() => {
    const checkTrialStatus = async () => {
      if (!isLoggedIn) return;
      
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user?.email) return;

        const { data: schoolUser } = await supabase
          .from('school_users')
          .select('school_id')
          .eq('email', user.email)
          .maybeSingle();

        if (!schoolUser?.school_id) return;

        const { data: trial } = await supabase
          .from('school_trials')
          .select('id')
          .eq('school_id', schoolUser.school_id)
          .maybeSingle();

        setHasUsedTrial(!!trial);
      } catch (error) {
        console.error('Error checking trial status:', error);
      }
    };

    checkTrialStatus();
  }, [isLoggedIn]);

  // Fetch discounts for each plan
  useEffect(() => {
    const fetchDiscounts = async () => {
      if (!schoolId || plans.length === 0) {
        console.log('Missing required data for discount fetch:', { schoolId, plansCount: plans.length });
        return;
      }
      
      setLoadingDiscounts(true);
      const discounts: Record<string, any> = {};
      
      try {
        console.log('Fetching discounts for schoolId:', schoolId, 'productId:', productId);
        
        for (const plan of plans) {
          console.log(`Checking discount for plan: ${plan.plan_id} (${plan.plan_name})`);
          const discount = await getApplicableDiscount(schoolId, productId, plan.plan_id);
          
          if (discount) {
            console.log(`Found discount for plan ${plan.plan_id} (${plan.plan_name}):`, discount);
            discounts[plan.plan_id] = discount;
          } else {
            console.log(`No discount found for plan ${plan.plan_id} (${plan.plan_name})`);
          }
        }
        
        console.log('Final plan discounts:', discounts);
        setPlanDiscounts(discounts);
      } catch (error) {
        console.error('Error fetching discounts:', error);
      } finally {
        setLoadingDiscounts(false);
      }
    };

    fetchDiscounts();
  }, [schoolId, productId, plans]);

  // Filter out Free Trial plan if already used
  const filteredPlans = plans.filter(plan => {
    if (plan.plan_name === 'Free Trial' && hasUsedTrial) {
      return false;
    }
    return true;
  });

  return (
    <div className="md:w-1/4 p-6 bg-gray-50 overflow-y-auto">
      <h2 className="text-xl font-bold mb-4">Pilihan Paket</h2>
      
      {!isLoggedIn && (
        <p className="text-red-500 mb-4">
          Please <a href="/auth" className="underline font-medium">sign in</a> to subscribe to any plan.
        </p>
      )}
      
      {loading || loadingDiscounts ? (
        <p className="text-center py-8">Loading plans...</p>
      ) : filteredPlans.length === 0 ? (
        <p className="text-center py-8">No plans available for this product.</p>
      ) : (
        <div className="space-y-4">
          {filteredPlans.map((plan) => {
            const planDiscount = planDiscounts[plan.plan_id];
            
            return (
              <PlanCard 
                key={plan.plan_id} 
                plan={plan} 
                onSubscribe={onSubscribe} 
                subscribing={subscribing}
                isDisabled={!isLoggedIn || shouldDisablePlan(plan, productId, userSubscriptions)}
                disabledReason={!isLoggedIn ? "Please sign in to subscribe" : getDisabledReason(plan, productId, userSubscriptions)}
                discount={planDiscount || null}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
